import React, { useEffect, useState } from "react";
import { Animated, Modal, Pressable, ScrollView, Switch, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { s } from "../SustainTeamManagementScreen.styles";
import { p } from "../SustainTeamManagementScreen.styles";
import { TeamMember, EditFormState, FilterOption } from "./SustainTeamTypes";
import { useTheme } from "../../../theme";

interface Props {
  editMember: TeamMember | undefined;
  editForm: EditFormState | null;
  setEditForm: React.Dispatch<React.SetStateAction<EditFormState | null>>;
  panelVisible: boolean;
  slideAnim: Animated.Value;
  isSaving: boolean;
  saveError: string | null;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (v: boolean) => void;
  showLeadWarning: boolean;
  setShowLeadWarning: (v: boolean) => void;
  showRoleDropdown: boolean;
  setShowRoleDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  isCurrentUserLead: boolean;
  /** Sustain Team members with role "lead", selectable as this member's manager. */
  leadOptions: { email: string; name: string; teamId: number | null }[];
  /** TKT_Teams list for the Team dropdown. */
  teamOptions: FilterOption[];
  /** Teams currently eligible for new assignments (active or with open tickets). */
  teamAssignmentOptions: FilterOption[];
  /** Server-side "team not found" validation message (400), shown under the Team field. */
  teamFieldError: string | null;
  closeEdit: () => void;
  saveEdit: () => void;
  discardAndClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const URGENCY_OPTIONS = [
  { value: "Critical", color: "#C23603", bg: "#FFE6E4", dot: "#C23603" },
  { value: "High",     color: "#C56F12", bg: "#FFE9D1", dot: "#D0781D" },
  { value: "Medium",   color: "#8B6508", bg: "#FFF6CC", dot: "#8B6508" },
  { value: "Low",      color: "#027C06", bg: "#E9F6EA", dot: "#027C06" },
];

const EditMemberPanel: React.FC<Props> = ({
  editMember,
  editForm,
  setEditForm,
  panelVisible,
  slideAnim,
  isSaving,
  saveError,
  showUnsavedDialog,
  setShowUnsavedDialog,
  showLeadWarning,
  setShowLeadWarning,
  showRoleDropdown,
  setShowRoleDropdown,
  isCurrentUserLead,
  leadOptions,
  teamOptions,
  teamAssignmentOptions,
  teamFieldError,
  closeEdit,
  saveEdit,
  discardAndClose,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState(false);
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);

  useEffect(() => {
    if (panelVisible) {
      setUrgencyDropdownOpen(false);
      setManagerDropdownOpen(false);
      setTeamDropdownOpen(false);
    }
  }, [panelVisible, editMember?.id]);

  // ── Validation ──────────────────────────────────────────────────────────────
  const managerEmailValue = editForm?.managerEmail ?? "";
  const managerEmailValid = managerEmailValue.trim() === "" || EMAIL_RE.test(managerEmailValue.trim());
  const teamValid = editForm?.teamId != null;
  const onDutyChannelsValid  = editForm ? (editForm.channelsOnDuty.nav  || editForm.channelsOnDuty.email  || editForm.channelsOnDuty.teams)  : true;
  const offDutyChannelsValid = editForm ? (editForm.channelsOffDuty.nav || editForm.channelsOffDuty.email || editForm.channelsOffDuty.teams) : true;
  const onDutyTriggersValid  = editForm ? (editForm.triggersOnDuty.newTicket  || editForm.triggersOnDuty.assigned  || editForm.triggersOnDuty.unassigned  || editForm.triggersOnDuty.comment  || editForm.triggersOnDuty.agingTicket)  : true;
  const offDutyTriggersValid = editForm ? (editForm.triggersOffDuty.newTicket || editForm.triggersOffDuty.assigned || editForm.triggersOffDuty.unassigned || editForm.triggersOffDuty.comment || editForm.triggersOffDuty.agingTicket) : true;
  const isFormValid = onDutyChannelsValid && offDutyChannelsValid && onDutyTriggersValid && offDutyTriggersValid && managerEmailValid && teamValid;

  const filteredLeadOptions = leadOptions.filter((l) => {
    if (editMember && l.email.toLowerCase() === editMember.email.toLowerCase()) return false;
    if (l.teamId !== (editForm?.teamId ?? null)) return false;
    const q = managerEmailValue.trim().toLowerCase();
    if (!q) return true;
    return l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q);
  });
  const inactiveTeamAssignment = editForm?.teamId != null
    && teamOptions.some((team) => team.value === String(editForm.teamId))
    && !teamAssignmentOptions.some((team) => team.value === String(editForm.teamId));
  return (
  <Modal
    visible={panelVisible}
    transparent
    animationType="none"
    onRequestClose={closeEdit}
  >
    <View style={p.overlay}>
      <Pressable style={p.backdrop} onPress={closeEdit} />
      <Animated.View style={[p.panel, isDark && { backgroundColor: "#202020" }, { transform: [{ translateX: slideAnim }] }]}>
        {editMember && editForm && (
          <>
            {/* Panel header */}
            <View style={[p.panelHeader, isDark && { borderBottomColor: "#363636" }]}>
              <View style={[p.panelAvatar, { backgroundColor: editMember.avatarColor }]}>
                <Text style={p.panelAvatarText}>{editMember.initials}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[p.panelName, isDark && { color: "#F2F2F2" }]}>{editMember.name}</Text>
                <Text style={[p.panelEmail, isDark && { color: "#B0B0B0" }]}>{editMember.email}</Text>
              </View>
              <Pressable style={p.panelClose} onPress={closeEdit} disabled={isSaving}>
                <MaterialIcons name="close" size={20} color={isSaving ? "#C0C0C0" : (isDark ? "#B0B0B0" : "#5a5a5a")} />
              </Pressable>
            </View>

            <ScrollView style={[p.panelBody, isDark && { backgroundColor: "#202020" }]} contentContainerStyle={{ paddingBottom: 32 }}>

              {/* ── Role ── */}
              <View style={[p.section, isDark && { borderBottomColor: "#363636" }]}>
                <View style={p.sectionTitleRow}>
                  <MaterialIcons name="person" size={15} color={isDark ? "#3D8BFF" : "#034895"} />
                  <Text style={[p.sectionTitle, isDark && { color: "#F2F2F2" }]}>Role</Text>
                </View>

                {isCurrentUserLead ? (
                  <>
                    <Pressable
                      style={[p.dropdown, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}
                      onPress={() => setShowRoleDropdown((v) => !v)}
                    >
                      <Text style={[p.dropdownText, isDark && { color: "#F2F2F2" }]}>
                        {editForm.role === "lead" ? "Lead" : "Member"}
                      </Text>
                      <MaterialIcons
                        name={showRoleDropdown ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                        size={20}
                        color={isDark ? "#B0B0B0" : "#4b4b4b"}
                      />
                    </Pressable>
                    {showRoleDropdown && (
                      <View style={[p.dropdownMenu, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}>
                        {(["lead", "member"] as const).map((r) => (
                          <Pressable
                            key={r}
                            style={[p.dropdownItem, editForm.role === r && (isDark ? { backgroundColor: "#0d2a44" } : p.dropdownItemActive)]}
                            onPress={() => {
                              setEditForm((f) => f ? { ...f, role: r } : f);
                              setShowRoleDropdown(false);
                            }}
                          >
                            <Text style={[p.dropdownItemText, isDark && { color: "#F2F2F2" }, editForm.role === r && p.dropdownItemTextActive]}>
                              {r === "lead" ? "Lead" : "Member"}
                            </Text>
                            {editForm.role === r && <MaterialIcons name="check" size={14} color={isDark ? "#3D8BFF" : "#034895"} />}
                          </Pressable>
                        ))}
                      </View>
                    )}
                    <Text style={[p.helpText, isDark && { color: "#6E6E6E" }]}>
                      Leads can edit other members and pause the Active Resolution Timer on tickets. At least one Lead must remain assigned at all times.
                    </Text>
                  </>
                ) : (
                  <>
                    <View style={[s.roleBadge, editForm.role === "lead" ? s.roleLead : s.roleMember, { alignSelf: "flex-start" }]}>
                      <Text style={[s.roleBadgeText, editForm.role === "lead" ? s.roleLeadText : s.roleMemberText]}>
                        {editForm.role === "lead" ? "Lead" : "Member"}
                      </Text>
                    </View>
                    <Text style={[p.helpText, isDark && { color: "#6E6E6E" }]}>Role changes can only be made by a Sustain Team Lead.</Text>
                  </>
                )}
              </View>

              {/* ── Team ── */}
              <View style={p.section}>
                <View style={p.sectionTitleRow}>
                  <MaterialIcons name="groups" size={15} color="#034895" />
                  <Text style={p.sectionTitle}>Team</Text>
                </View>

                <Pressable
                  style={[p.dropdown, !teamValid && { borderColor: "#C12C01" }]}
                  onPress={() => setTeamDropdownOpen((v) => !v)}
                >
                  <Text style={p.dropdownText}>
                    {editForm.teamId != null
                      ? (teamOptions.find((t) => t.value === String(editForm.teamId))?.label ?? "Unknown team")
                      : "Select a team…"}
                  </Text>
                  <MaterialIcons
                    name={teamDropdownOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                    size={20}
                    color="#4b4b4b"
                  />
                </Pressable>

                {inactiveTeamAssignment && (
                  <Text style={[p.helpText, { color: "#C12C01", marginTop: 6 }]}>
                    Inactive team Assignment
                  </Text>
                )}

                {teamDropdownOpen && (
                  <View style={p.dropdownMenu}>
                    {teamAssignmentOptions.length === 0 ? (
                      <View style={p.dropdownItem}>
                        <Text style={[p.dropdownItemText, { color: "#9a9a9a" }]}>No teams available</Text>
                      </View>
                    ) : (
                      teamAssignmentOptions.map((team) => {
                        const active = editForm.teamId === Number(team.value);
                        return (
                          <Pressable
                            key={team.value}
                            style={[p.dropdownItem, active && p.dropdownItemActive]}
                            onPress={() => {
                              const nextTeamId = Number(team.value);
                              setEditForm((f) => {
                                if (!f) return f;
                                // Manager must be a Lead on the newly-selected team; clear if no longer valid.
                                const managerStillValid = leadOptions.some(
                                  (l) => l.teamId === nextTeamId && l.email.toLowerCase() === f.managerEmail.trim().toLowerCase(),
                                );
                                return { ...f, teamId: nextTeamId, managerEmail: managerStillValid ? f.managerEmail : "" };
                              });
                              setTeamDropdownOpen(false);
                            }}
                          >
                            <Text style={[p.dropdownItemText, active && p.dropdownItemTextActive]}>{team.label}</Text>
                            {active && <MaterialIcons name="check" size={14} color="#034895" />}
                          </Pressable>
                        );
                      })
                    )}
                  </View>
                )}

                {!teamValid && (
                  <Text style={[p.helpText, { color: "#C12C01", marginTop: 6 }]}>Team is required.</Text>
                )}
                {teamFieldError && (
                  <Text style={[p.helpText, { color: "#C12C01", marginTop: 6 }]}>{teamFieldError}</Text>
                )}
                <Text style={p.helpText}>
                  Required. Members only see tickets routed to their team in the Sustain Queue. Tickets route by category and subcategory per Ticketing Configurations.
                </Text>
              </View>

              {/* ── Manager ── */}
              <View style={p.section}>
                <View style={p.sectionTitleRow}>
                  <MaterialIcons name="supervisor-account" size={15} color="#034895" />
                  <Text style={p.sectionTitle}>Manager</Text>
                </View>

                <View style={{ position: "relative" }}>
                  <View style={[p.dropdown, !managerEmailValid && { borderColor: "#C12C01" }]}>
                    <TextInput
                      style={[p.dropdownText, { flex: 1, padding: 0 }]}
                      placeholder="No manager assigned"
                      placeholderTextColor="#9a9a9a"
                      value={managerEmailValue}
                      onChangeText={(v) => setEditForm((f) => (f ? { ...f, managerEmail: v } : f))}
                      onFocus={() => setManagerDropdownOpen(true)}
                    />
                    <Pressable onPress={() => setManagerDropdownOpen((v) => !v)}>
                      <MaterialIcons
                        name={managerDropdownOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                        size={20}
                        color="#4b4b4b"
                      />
                    </Pressable>
                  </View>

                  {managerDropdownOpen && (
                    <View style={p.dropdownMenu}>
                      <Pressable
                        style={p.dropdownItem}
                        onPress={() => {
                          setEditForm((f) => (f ? { ...f, managerEmail: "" } : f));
                          setManagerDropdownOpen(false);
                        }}
                      >
                        <Text style={[p.dropdownItemText, { color: "#9a9a9a" }]}>No manager</Text>
                      </Pressable>
                      {filteredLeadOptions.length === 0 ? (
                        <View style={p.dropdownItem}>
                          <Text style={[p.dropdownItemText, { color: "#9a9a9a" }]}>No matching Leads</Text>
                        </View>
                      ) : (
                        filteredLeadOptions.map((lead) => {
                          const active = managerEmailValue.trim().toLowerCase() === lead.email.toLowerCase();
                          return (
                            <Pressable
                              key={lead.email}
                                style={[p.dropdownItem, active && p.dropdownItemActive]}
                                onPress={() => {
                                  setEditForm((f) => (f ? { ...f, managerEmail: lead.email } : f));
                                  setManagerDropdownOpen(false);
                                }}
                              >
                                <Text style={[p.dropdownItemText, active && p.dropdownItemTextActive]}>
                                  {lead.name} ({lead.email})
                                </Text>
                                {active && <MaterialIcons name="check" size={14} color="#034895" />}
                              </Pressable>
                            );
                          })
                        )}
                      </View>
                    )}
                </View>

                {!managerEmailValid && (
                  <Text style={[p.helpText, { color: "#C12C01", marginTop: 6 }]}>
                    Enter a valid email address.
                  </Text>
                )}
                <Text style={p.helpText}>
                  Optional. Lists Leads on this member's team, excluding the current user. Manager is cleared if the Lead is changed to Member or moved off the team.
                </Text>
              </View>

              {/* ── Notification Preferences ── */}
              <View style={[p.section, isDark && { borderBottomColor: "#363636" }]}>
                <View style={p.sectionTitleRow}>
                  <MaterialIcons name="notifications" size={15} color={isDark ? "#3D8BFF" : "#034895"} />
                  <Text style={[p.sectionTitle, isDark && { color: "#F2F2F2" }]}>Notification Preferences</Text>
                </View>

                {/* ── Duty status toggle (shared for Channels + Triggers) ── */}
                <View style={p.dutyToggleRow}>
                  <View style={p.dutyToggleLeft}>
                    <View style={editForm.onDuty ? p.dutyBadgeOn : p.dutyBadgeOff}>
                      <Text style={editForm.onDuty ? p.dutyBadgeOnText : p.dutyBadgeOffText}>
                        {editForm.onDuty ? "On Duty" : "Off Duty"}
                      </Text>
                    </View>
                  </View>
                  <Switch
                    value={editForm.onDuty}
                    onValueChange={(v) =>
                      setEditForm((f) => (f ? { ...f, onDuty: v } : f))
                    }
                    trackColor={{ false: "#D1D5DB", true: "#15803D" }}
                    thumbColor="#ffffff"
                    ios_backgroundColor="#D1D5DB"
                  />
                </View>

                <Text style={[p.subsectionTitle, isDark && { color: "#6E6E6E" }]}>CHANNELS</Text>

                <View style={[p.channelList, isDark && { borderColor: "#363636" }, { marginTop: 10 }]}>
                  {[
                    { key: "nav"   as const, icon: "dashboard",           label: "Navigator In-App",  sub: "Toasts, notification center, and homepage banners" },
                    { key: "email" as const, icon: "mail-outline",         label: "Email",             sub: "Delivered to the member's work email" },
                    { key: "teams" as const, icon: "chat-bubble-outline",  label: "Microsoft Teams",   sub: "Delivered via Teams chat bot" },
                  ].map(({ key, icon, label, sub }, idx, arr) => (
                    <View
                      key={key}
                      style={[p.channelRow, isDark && { backgroundColor: "#202020" }, idx < arr.length - 1 && (isDark ? { ...p.channelRowBorder, borderBottomColor: "#363636" } : p.channelRowBorder)]}
                    >
                      <View style={[p.channelIconWrap, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}>
                        <MaterialIcons name={icon as any} size={15} color={isDark ? "#3D8BFF" : "#034895"} />
                      </View>
                      <View style={p.channelInfo}>
                        <Text style={[p.channelLabel, isDark && { color: "#F2F2F2" }]}>{label}</Text>
                        <Text style={[p.channelSub, isDark && { color: "#6E6E6E" }]}>{sub}</Text>
                      </View>
                      <Switch
                        value={editForm.onDuty ? editForm.channelsOnDuty[key] : editForm.channelsOffDuty[key]}
                        onValueChange={(v) =>
                          setEditForm((f) => {
                            if (!f) return f;
                            return editForm.onDuty
                              ? { ...f, channelsOnDuty:  { ...f.channelsOnDuty,  [key]: v } }
                              : { ...f, channelsOffDuty: { ...f.channelsOffDuty, [key]: v } };
                          })
                        }
                        trackColor={{ false: "#D1D5DB", true: "#1D4ED8" }}
                        thumbColor="#ffffff"
                        ios_backgroundColor="#D1D5DB"
                      />
                    </View>
                  ))}
                </View>

                {(!onDutyChannelsValid || !offDutyChannelsValid) && (
                  <View style={p.channelWarning}>
                    <MaterialIcons name="error-outline" size={14} color="#b45309" />
                    <View style={{ flex: 1, gap: 2 }}>
                      {!onDutyChannelsValid && (
                        <Text style={p.channelWarningText}>On Duty: select at least one notification channel.</Text>
                      )}
                      {!offDutyChannelsValid && (
                        <Text style={p.channelWarningText}>Off Duty: select at least one notification channel.</Text>
                      )}
                    </View>
                  </View>
                )}

                <Text style={[p.subsectionTitle, isDark && { color: "#6E6E6E" }, { marginTop: 20 }]}>TRIGGERS</Text>
                {[
                  { key: "newTicket"  as const, label: "New Ticket Submitted",                sub: "Notify when any new ticket is submitted to the queue" },
                  { key: "assigned"   as const, label: "Ticket Assigned to Me",               sub: "Notify when a ticket is assigned or reassigned to this member" },
                  { key: "unassigned" as const, label: "Ticket Unassigned from Me",           sub: "Notify when a ticket is reassigned away from this member" },
                  { key: "comment"    as const, label: "Comment Added to My Assigned Ticket", sub: "Notify when another user comments on a ticket assigned to this member" },
                  { key: "agingTicket" as const, label: "Aging Ticket Notification",          sub: "Notify when a ticket is aging past the response threshold" },
                ].map(({ key, label, sub }) => {
                  const checked = editForm.onDuty
                    ? editForm.triggersOnDuty[key]
                    : editForm.triggersOffDuty[key];
                  return (
                    <Pressable
                      key={key}
                      style={[p.checkRow, isDark && { borderBottomColor: "#363636" }]}
                      onPress={() =>
                        setEditForm((f) => {
                          if (!f) return f;
                          return editForm.onDuty
                            ? { ...f, triggersOnDuty:  { ...f.triggersOnDuty,  [key]: !f.triggersOnDuty[key]  } }
                            : { ...f, triggersOffDuty: { ...f.triggersOffDuty, [key]: !f.triggersOffDuty[key] } };
                        })
                      }
                    >
                      <View style={[p.checkbox, isDark && !checked && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, checked && p.checkboxChecked]}>
                        {checked && <MaterialIcons name="check" size={11} color="#fff" />}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={[p.checkLabel, isDark && { color: "#F2F2F2" }]}>{label}</Text>
                        <Text style={[p.checkSub, isDark && { color: "#6E6E6E" }]}>{sub}</Text>
                      </View>
                    </Pressable>
                  );
                })}

                {(!onDutyTriggersValid || !offDutyTriggersValid) && (
                  <View style={[p.channelWarning, { marginTop: 6 }]}>
                    <MaterialIcons name="error-outline" size={14} color="#b45309" />
                    <View style={{ flex: 1, gap: 2 }}>
                      {!onDutyTriggersValid && (
                        <Text style={p.channelWarningText}>On Duty: select at least one trigger to proceed.</Text>
                      )}
                      {!offDutyTriggersValid && (
                        <Text style={p.channelWarningText}>Off Duty: select at least one trigger to proceed.</Text>
                      )}
                    </View>
                  </View>
                )}

                <Text style={[p.subsectionTitle, isDark && { color: "#6E6E6E" }, { marginTop: 20 }]}>SCOPE</Text>
                <Text style={[p.helpText, isDark && { color: "#6E6E6E" }, { marginBottom: 8 }]}>
                  Applies to "New Ticket Submitted" only
                </Text>
                {[
                  { value: "all"      as const, label: "All Tickets",           sub: "Receive notifications for every ticket submitted regardless of assignment" },
                  { value: "assigned" as const, label: "Assigned Tickets Only", sub: "Receive notifications only for tickets assigned to this member" },
                  { value: "custom"   as const, label: "Custom",                sub: "Define specific urgency levels to receive alerts for" },
                ].map(({ value, label, sub }) => (
                  <Pressable
                    key={value}
                    style={[p.checkRow, isDark && { borderBottomColor: "#363636" }]}
                    onPress={() => {
                      setEditForm((f) => f ? { ...f, scope: value } : f);
                      if (value !== "custom") setUrgencyDropdownOpen(false);
                    }}
                  >
                    <View style={[p.radioOuter, isDark && { borderColor: "#6E6E6E" }, editForm.scope === value && p.radioOuterActive]}>
                      {editForm.scope === value && <View style={p.radioInner} />}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[p.checkLabel, isDark && { color: "#F2F2F2" }]}>{label}</Text>
                      <Text style={[p.checkSub, isDark && { color: "#6E6E6E" }]}>{sub}</Text>
                    </View>
                  </Pressable>
                ))}

                {/* ── Custom scope: urgency multi-select ── */}
                {editForm.scope === "custom" && (
                  <View style={{ marginTop: 6, marginLeft: 28, marginBottom: 4 }}>
                    {/* Dropdown trigger */}
                    <Pressable
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: urgencyDropdownOpen ? "#034895" : "#d1d5db",
                        borderRadius: 6,
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                        backgroundColor: "#ffffff",
                        justifyContent: "space-between",
                      }}
                      onPress={() => setUrgencyDropdownOpen((v) => !v)}
                    >
                      <View style={{ flex: 1, flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
                        {(editForm.customUrgencies ?? []).length === 0 ? (
                          <Text style={{ fontSize: 13, color: "#9099aa" }}>Select urgency levels…</Text>
                        ) : (
                          (editForm.customUrgencies ?? []).map((u) => {
                            const opt = URGENCY_OPTIONS.find((o) => o.value === u);
                            return (
                              <View
                                key={u}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  backgroundColor: opt?.bg ?? "#f0f0f0",
                                  borderRadius: 100,
                                  paddingHorizontal: 8,
                                  paddingVertical: 2,
                                  gap: 4,
                                }}
                              >
                                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: opt?.dot ?? "#888" }} />
                                <Text style={{ fontSize: 12, color: opt?.color ?? "#333", fontWeight: "600" }}>{u}</Text>
                              </View>
                            );
                          })
                        )}
                      </View>
                      <MaterialIcons
                        name={urgencyDropdownOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                        size={18}
                        color="#4b4b4b"
                      />
                    </Pressable>

                    {/* Dropdown menu — multi-select */}
                    {urgencyDropdownOpen && (
                      <View
                        style={{
                          borderWidth: 1,
                          borderColor: "#d1d5db",
                          borderRadius: 6,
                          backgroundColor: "#ffffff",
                          marginTop: 2,
                          overflow: "hidden",
                        }}
                      >
                        {URGENCY_OPTIONS.map((opt) => {
                          const selected = (editForm.customUrgencies ?? []).includes(opt.value);
                          return (
                            <Pressable
                              key={opt.value}
                              style={[{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingHorizontal: 12,
                                paddingVertical: 10,
                                gap: 10,
                              }, selected && { backgroundColor: "#EEF5FF" }]}
                              onPress={() =>
                                setEditForm((f) => {
                                  if (!f) return f;
                                  const prev = f.customUrgencies ?? [];
                                  const next = selected
                                    ? prev.filter((u) => u !== opt.value)
                                    : [...prev, opt.value];
                                  return { ...f, customUrgencies: next };
                                })
                              }
                            >
                              <View style={[p.checkbox, selected && p.checkboxChecked]}>
                                {selected && <MaterialIcons name="check" size={11} color="#fff" />}
                              </View>
                              <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flex: 1 }}>
                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: opt.dot }} />
                                <Text style={{ fontSize: 13, color: selected ? "#034895" : "#2d2d2d", fontWeight: selected ? "600" : "400" }}>
                                  {opt.value}
                                </Text>
                              </View>
                            </Pressable>
                          );
                        })}
                      </View>
                    )}

                    {/* Validation hint */}
                    {(editForm.customUrgencies ?? []).length === 0 && (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 4, marginTop: 4 }}>
                        <MaterialIcons name="info-outline" size={13} color="#b45309" />
                        <Text style={{ fontSize: 11, color: "#b45309" }}>Select at least one urgency level.</Text>
                      </View>
                    )}
                  </View>
                )}
              </View>

            </ScrollView>

            {/* Inline save error banner */}
            {saveError ? (
              <View style={p.saveErrorBanner}>
                <MaterialIcons name="error-outline" size={14} color="#C12C01" />
                <Text style={p.saveErrorText}>{saveError}</Text>
              </View>
            ) : null}

            {/* Panel footer */}
            <View style={[p.panelFooter, isDark && { borderTopColor: "#363636" }]}>
              <Pressable style={p.btnGhost} onPress={closeEdit} disabled={isSaving}>
                <Text style={[p.btnGhostText, isDark && { color: "#B0B0B0" }, isSaving && { opacity: 0.4 }]}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[p.btnPrimary, (isSaving || !isFormValid) && { opacity: 0.6 }]}
                onPress={saveEdit}
                disabled={isSaving || !isFormValid}
              >
                <Text style={p.btnPrimaryText}>{isSaving ? "Saving…" : "Save Changes"}</Text>
              </Pressable>
            </View>

            {/* Unsaved changes confirmation dialog */}
            {showUnsavedDialog && (
              <View style={p.dialogBackdrop}>
                <View style={[p.dialog, isDark && { backgroundColor: "#202020" }]}>
                  <Text style={[p.dialogTitle, isDark && { color: "#F2F2F2" }]}>Unsaved Changes</Text>
                  <Text style={[p.dialogMsg, isDark && { color: "#B0B0B0" }]}>You have unsaved changes. Discard and close?</Text>
                  <View style={p.dialogActions}>
                    <Pressable style={[p.dialogBtnGhost, isDark && { borderColor: "#404040" }]} onPress={() => setShowUnsavedDialog(false)}>
                      <Text style={[p.dialogBtnGhostText, isDark && { color: "#F2F2F2" }]}>Keep Editing</Text>
                    </Pressable>
                    <Pressable style={p.dialogBtnDanger} onPress={discardAndClose}>
                      <Text style={p.dialogBtnDangerText}>Discard Changes</Text>
                    </Pressable>
                  </View>
                </View>
              </View>
            )}

            {/* Lead demotion blocked dialog */}
            {showLeadWarning && (
              <View style={p.dialogBackdrop}>
                <View style={[p.dialog, isDark && { backgroundColor: "#202020" }]}>
                  <MaterialIcons name="warning" size={26} color="#b45309" style={{ marginBottom: 8, alignSelf: "center" }} />
                  <Text style={[p.dialogTitle, isDark && { color: "#F2F2F2" }]}>Cannot Change Role</Text>
                  <Text style={[p.dialogMsg, isDark && { color: "#B0B0B0" }]}>
                    At least one Lead must be assigned at all times. Assign another Lead before changing this role.
                  </Text>
                  <View style={p.dialogActions}>
                    <Pressable style={[p.dialogBtnGhost, { flex: 1 }, isDark && { borderColor: "#404040" }]} onPress={() => setShowLeadWarning(false)}>
                      <Text style={[p.dialogBtnGhostText, isDark && { color: "#F2F2F2" }]}>Got It</Text>
                    </Pressable>
                  </View>
                </View>
              </View>
            )}
          </>
        )}
      </Animated.View>
    </View>
  </Modal>
  );
};

export default EditMemberPanel;
